/**
 * The colours of the system, chosen by whoever is using it.
 *
 * Everything on screen is painted from a handful of custom properties, so a
 * theme is a handful of numbers rather than a stylesheet: a brand colour, a
 * second colour for charts, and how much the greys lean towards a hue. From
 * those, a ten-step ramp and both light and dark surfaces are generated in
 * OKLCH, which is what keeps a picked colour from turning muddy three steps
 * along.
 *
 * The choice is kept on the device rather than on the server, because it is a
 * preference of the person at this screen — the shop's tablet at the counter
 * and the owner's laptop can differ, and neither needs the other's taste.
 */

import { hexToOklch, isHex, normaliseHex, oklchToHex, worstDeltaE } from '@/lib/colour';

export interface Appearance {
  /** Buttons, links, the active nav item — everything that says "this one". */
  brand: string;
  /** The second chart series, and anything that has to sit beside the brand. */
  accent: string;
  /** How far the greys lean towards a hue, 0 (neutral) to 0.02 (tinted). */
  neutralChroma: number;
  /** Which hue they lean towards. */
  neutralHue: number;
}

export const DEFAULT_APPEARANCE: Appearance = {
  brand: '#3b6ef5',
  accent: '#0f9d76',
  neutralChroma: 0.015,
  neutralHue: 260,
};

/** Whole themes, for someone who wants a different look and not a project. */
export const THEME_PRESETS: { name: string; description: string; appearance: Appearance }[] = [
  {
    name: 'Stockpilot',
    description: 'The default: a cool blue with a green second series.',
    appearance: DEFAULT_APPEARANCE,
  },
  {
    // Green with gold is the obvious pairing and the wrong one: on a chart the
    // two are hard to separate for a red-green colour-blind reader.
    name: 'Matoke',
    description: 'Green and violet, warm greys.',
    appearance: { brand: '#1f7a4d', accent: '#7c3aed', neutralChroma: 0.012, neutralHue: 120 },
  },
  {
    name: 'Kampala clay',
    description: 'Terracotta and teal, earthy greys.',
    appearance: { brand: '#b5502a', accent: '#1f7f8c', neutralChroma: 0.014, neutralHue: 40 },
  },
  {
    name: 'Midnight',
    description: 'Indigo and amber, cool greys.',
    appearance: { brand: '#4c4fd4', accent: '#c07d0a', neutralChroma: 0.018, neutralHue: 280 },
  },
  {
    name: 'Plain',
    description: 'Graphite and amber, no tint at all.',
    appearance: { brand: '#3f4753', accent: '#b06d0a', neutralChroma: 0, neutralHue: 0 },
  },
];

/**
 * The lightness and chroma of each step of the ramp.
 *
 * These are the shape of the ramp, not its colour: the hue comes from whatever
 * was picked, and the chroma is scaled by how colourful that pick was, so a
 * muted grey-blue produces a muted ramp rather than a lurid one.
 */
const RAMP: { step: number; l: number; c: number }[] = [
  { step: 50, l: 0.97, c: 0.02 },
  { step: 100, l: 0.94, c: 0.04 },
  { step: 200, l: 0.88, c: 0.07 },
  { step: 300, l: 0.8, c: 0.11 },
  { step: 400, l: 0.71, c: 0.15 },
  { step: 500, l: 0.62, c: 0.18 },
  { step: 600, l: 0.54, c: 0.19 },
  { step: 700, l: 0.46, c: 0.17 },
  { step: 800, l: 0.39, c: 0.14 },
  { step: 900, l: 0.33, c: 0.11 },
];

/** The step buttons and links are painted with — what has to stay readable. */
export const PRIMARY_STEP = 600;

export function brandRamp(hex: string): Record<number, string> {
  const base = hexToOklch(safeHex(hex, DEFAULT_APPEARANCE.brand));
  // A pick at full chroma keeps the ramp as designed; a duller pick scales the
  // whole ramp down with it rather than being "corrected" to something else.
  const scale = Math.min(1.15, Math.max(0.3, base.c / 0.19));

  return Object.fromEntries(
    RAMP.map(({ step, l, c }) => [step, oklchToHex({ l, c: c * scale, h: base.h })]),
  );
}

/** The two chart series, stepped separately for each mode's own surface. */
export function chartColours(
  appearance: Appearance,
  mode: 'light' | 'dark',
): { chart1: string; chart2: string } {
  const target = mode === 'light' ? { l: 0.58, c: 0.16 } : { l: 0.67, c: 0.14 };
  const brand = hexToOklch(safeHex(appearance.brand, DEFAULT_APPEARANCE.brand));
  const accent = hexToOklch(safeHex(appearance.accent, DEFAULT_APPEARANCE.accent));

  return {
    chart1: oklchToHex({ ...target, h: brand.h }),
    chart2: oklchToHex({ ...target, h: accent.h }),
  };
}

interface Surface {
  surface: number;
  raised: number;
  sunken: number;
  border: number;
  primary: number;
  secondary: number;
  muted: number;
}

const LIGHT: Surface = {
  surface: 0.99,
  raised: 1,
  sunken: 0.97,
  border: 0.91,
  primary: 0.22,
  secondary: 0.5,
  muted: 0.62,
};

const DARK: Surface = {
  surface: 0.19,
  raised: 0.24,
  sunken: 0.16,
  border: 0.32,
  primary: 0.96,
  secondary: 0.75,
  muted: 0.62,
};

function surfaces(appearance: Appearance, mode: 'light' | 'dark'): Record<string, string> {
  const steps = mode === 'light' ? LIGHT : DARK;
  const h = appearance.neutralHue;
  const tint = Math.min(0.02, Math.max(0, appearance.neutralChroma));

  // Text carries a little more tint than the surfaces it sits on, which is
  // what stops a tinted theme reading as a grey page with coloured paper.
  const grey = (l: number, factor: number): string => oklchToHex({ l, c: tint * factor, h });

  return {
    '--surface': grey(steps.surface, mode === 'light' ? 0.13 : 1),
    '--surface-raised': grey(steps.raised, mode === 'light' ? 0 : 1.2),
    '--surface-sunken': grey(steps.sunken, mode === 'light' ? 0.27 : 0.93),
    '--border-subtle': grey(steps.border, mode === 'light' ? 0.4 : 1.2),
    '--text-primary': grey(steps.primary, 1.33),
    '--text-secondary': grey(steps.secondary, 1.33),
    '--text-muted': grey(steps.muted, 1),
  };
}

/** The whole theme as a stylesheet, light and dark together. */
export function themeCss(appearance: Appearance): string {
  const ramp = brandRamp(appearance.brand);
  const light = chartColours(appearance, 'light');
  const dark = chartColours(appearance, 'dark');

  const rampLines = Object.entries(ramp)
    .map(([step, hex]) => `  --color-brand-${step}: ${hex};`)
    .join('\n');

  const block = (entries: Record<string, string>): string =>
    Object.entries(entries)
      .map(([name, value]) => `  ${name}: ${value};`)
      .join('\n');

  return [
    ':root {',
    rampLines,
    block(surfaces(appearance, 'light')),
    `  --chart-1: ${light.chart1};`,
    `  --chart-2: ${light.chart2};`,
    '}',
    'html.dark {',
    block(surfaces(appearance, 'dark')),
    `  --chart-1: ${dark.chart1};`,
    `  --chart-2: ${dark.chart2};`,
    '}',
  ].join('\n');
}

const STYLE_ID = 'sp-appearance';

/**
 * Puts the theme on the page.
 *
 * A stylesheet rather than inline properties on the root element, because dark
 * mode is a class on that same element: only a rule can say "these values, but
 * those ones when dark".
 */
export function applyAppearance(appearance: Appearance, root: Document = document): void {
  let style = root.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = root.createElement('style');
    style.id = STYLE_ID;
    root.head.append(style);
  }
  style.textContent = themeCss(appearance);
}

export interface ThemeWarning {
  field: 'accent';
  message: string;
}

/**
 * What is wrong with a theme, measured rather than guessed.
 *
 * Readability is not checked here because it is guaranteed instead: whatever
 * colour is picked, the ramp restates it at a fixed lightness, so white text
 * on a button keeps its contrast whichever hue someone chose. What the ramp
 * cannot guarantee is that two *different* hues stay apart for a red-green
 * colour-blind reader — green and gold is the pairing everyone reaches for and
 * the one that fails — so that is measured, and said.
 */
export function checkAppearance(appearance: Appearance): ThemeWarning[] {
  const warnings: ThemeWarning[] = [];

  const light = chartColours(appearance, 'light');
  const separation = worstDeltaE(light.chart1, light.chart2);
  if (separation < 8) {
    warnings.push({
      field: 'accent',
      message:
        'On a chart, these two colours are hard to tell apart for a red-green colour-blind reader '
        + `(difference ${separation.toFixed(1)}, against a target of 8). Move the second colour `
        + 'further round the wheel.',
    });
  }

  return warnings;
}

// ---- storage -------------------------------------------------------------

const KEY = 'sp.appearance';

export function readAppearance(): Appearance {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return DEFAULT_APPEARANCE;

    const parsed = JSON.parse(stored) as Partial<Appearance>;
    return {
      brand: safeHex(parsed.brand, DEFAULT_APPEARANCE.brand),
      accent: safeHex(parsed.accent, DEFAULT_APPEARANCE.accent),
      neutralChroma:
        typeof parsed.neutralChroma === 'number' && parsed.neutralChroma >= 0
          ? Math.min(0.02, parsed.neutralChroma)
          : DEFAULT_APPEARANCE.neutralChroma,
      neutralHue:
        typeof parsed.neutralHue === 'number'
          ? ((parsed.neutralHue % 360) + 360) % 360
          : DEFAULT_APPEARANCE.neutralHue,
    };
  } catch {
    // Unavailable storage, or something else wrote nonsense to the key: the
    // default theme is always a working answer.
    return DEFAULT_APPEARANCE;
  }
}

export function writeAppearance(appearance: Appearance): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(appearance));
  } catch {
    /* storage unavailable — the theme simply will not outlive this tab */
  }
}

export function clearAppearance(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear if nothing could be stored */
  }
}

export function isDefault(appearance: Appearance): boolean {
  return (
    normaliseHex(appearance.brand) === DEFAULT_APPEARANCE.brand &&
    normaliseHex(appearance.accent) === DEFAULT_APPEARANCE.accent &&
    appearance.neutralChroma === DEFAULT_APPEARANCE.neutralChroma &&
    appearance.neutralHue === DEFAULT_APPEARANCE.neutralHue
  );
}

function safeHex(value: string | undefined, fallback: string): string {
  return value && isHex(value) ? normaliseHex(value) : fallback;
}
